import { queryClient } from '@/lib/api-client'
import { getAssetUrl } from '@/config/assetUrl'
import { StageLocations, SingleLocation } from '@/routes/projectStages/issue/types/issue'

const SVG_QUERY_KEY_PREFIX = 'svgAsset'

/**
 * Fetches SVG text content with React Query persistence.
 * Checks the queryClient cache first (persisted to IndexedDB via idb-keyval),
 * then falls back to network fetch. Result is stored in the cache for offline access.
 */
export async function getSvgText(
  contentUrl: string,
  signal?: AbortSignal,
): Promise<string> {
  const cached = queryClient.getQueryData<string>([SVG_QUERY_KEY_PREFIX, contentUrl])
  if (cached) {
    return cached
  }

  const resolvedUrl = getAssetUrl(contentUrl)
  const response = await fetch(resolvedUrl, {
    credentials: 'include',
    signal,
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch SVG: ${response.statusText}`)
  }

  const svgText = await response.text()

  queryClient.setQueryData([SVG_QUERY_KEY_PREFIX, contentUrl], svgText)

  return svgText
}

/**
 * Recursively walks a location tree and collects unique SVG contentUrl values.
 */
export function collectSvgUrlsFromLocations(locations: StageLocations): string[] {
  const urls = new Set<string>()

  function walk(location: SingleLocation): void {
    if (location.map?.contentUrl) {
      urls.add(location.map.contentUrl)
    }
    if (location.children) {
      for (const child of location.children) {
        walk(child as SingleLocation)
      }
    }
  }

  for (const location of locations) {
    walk(location)
  }

  return [...urls]
}
